<script setup lang="ts">
import { computed, ref } from 'vue'

import { classMerge, Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@toni/brand-ui'
import { BauerProductCard, PurchaseOptions, SUPPORTED_MARKETS } from '@toni/commerce-api-types'

import { getLowestPrice } from '../../utils/purchaseOptionsLowestPrice'
import { scrollToAnchor } from '../../utils/scroll'
import { useClient } from '../../utils/is-client'
import ReviewSection from '../ReviewSection.vue'
import PurchaseOption from '../PurchaseOption.vue'
import ProductLabel from '../ProductLabel.vue'
import ProductRatings from '../ProductRatings.vue'
import ImageGallery from '../ImageGallery.vue'
import AffiliateLink from '../AffiliateLink.vue'

const props = withDefaults(defineProps<{
  productCard: BauerProductCard,
  purchaseOptions?: PurchaseOptions,
  embedded?: boolean
}>(), {
  purchaseOptions: undefined,
  embedded: true
})

const purchaseOptions = computed(() => props.purchaseOptions || [])
const primaryPurchaseOption = purchaseOptions.value[0]
const alternativePurchaseOptions = purchaseOptions.value.slice(1)

const formattedAlternativePurchaseOptionsLowestPrice = getLowestPrice(primaryPurchaseOption, alternativePurchaseOptions)
const isAlternativePurchaseOptionsExpanded = ref(false)

const showExpandPurchaseOptionsLink = computed((): boolean => {
  return (!!alternativePurchaseOptions?.length && isAlternativePurchaseOptionsExpanded.value === false)
})
const alternativePurchaseOptionsAnchor = `${props.productCard.bauerProductCardId}-alternative`

const toggleExpand = (): boolean => (isAlternativePurchaseOptionsExpanded.value = !isAlternativePurchaseOptionsExpanded.value)

const { isClient } = useClient()

const images = computed(() => props.productCard.images?.filter((image) => !image.isHidden))

const scrollToAlternativePurchaseOptionsAndExpand = () => {
  scrollToAnchor(alternativePurchaseOptionsAnchor)
  isAlternativePurchaseOptionsExpanded.value = true
}
</script>

<template>
  <div :id="props.productCard.title">
    <AffiliateLink v-if="productCard.intendedUses?.some((el) => el.market && el.market === SUPPORTED_MARKETS.DE)"
                   :class="embedded ? 'pl-4' : ''" />
    <div :class="`grid col-span-2 gap-7 p-4 ${embedded ? '' : 'border-solid border-2 shadow-lg'}`">
      <ProductLabel v-if="productCard.label"
                    :text="productCard.label"
                    :highlight="true"
                    :invert-text="false" />
      <h3 class="font-heading text-center text-heading-sm text-foreground md:text-heading-lg">
        {{ productCard.title }}
      </h3>
      <p v-if="productCard.conclusion"
         class="font-ui text-ui-lg text-foreground whitespace-pre-wrap text-center">
        {{ productCard.conclusion }}
      </p>
      <ImageGallery v-if="images && images.length > 0"
                    :images="images" />
      <ProductRatings v-if="productCard.ratings"
                      :ratings="
                        productCard.ratings?.map((rating) => ({
                          label: rating.title,
                          value: rating.value,
                          total: rating.maxValue,
                          style: rating.style
                        }))
                      "
                      :is-comparison="false" />
      <div class="flex flex-col justify-between gap-2.5">
        <div class="md:order-1">
          <PurchaseOption v-if="primaryPurchaseOption"
                          :bauer-product-card-id="productCard.bauerProductCardId"
                          :purchase-option="primaryPurchaseOption"
                          variant="highlighted"
                          :purchase-option-position="1" />
        </div>
        <div class="md:hidden">
          <a href="#"
             class="text-action underline"
             @click.prevent="scrollToAlternativePurchaseOptionsAndExpand">
            {{ $t('commerce.otherOffers', {count: alternativePurchaseOptions?.length}) }}
          </a>
        </div>
        <div class="flex flex-wrap gap-7">
          <ReviewSection v-if="productCard.pros?.values.length"
                         type="pros"
                         :pro-cons="productCard.pros" />
          <ReviewSection v-if="productCard.cons?.values.length"
                         type="cons"
                         :pro-cons="productCard.cons" />
        </div>
        <h4 v-if="alternativePurchaseOptions.length > 0"
            :id="alternativePurchaseOptionsAnchor"
            class="font-heading mt-3 py-3 tracking-normal text-heading-sm border-t">
          {{ $t('commerce.purchaseOptions') }}
        </h4>
        <div v-if="alternativePurchaseOptions.length > 0"
             class="md:order-2 flex flex-col justify-between gap-2.5">
          <div class="md:hidden">
            <PurchaseOption v-if="primaryPurchaseOption"
                            :bauer-product-card-id="productCard.bauerProductCardId"
                            :purchase-option="primaryPurchaseOption"
                            :purchase-option-position="1" />
          </div>
          <a v-if="showExpandPurchaseOptionsLink"
             href="#"
             class="text-action underline"
             @click.prevent="toggleExpand">
            {{ $t('commerce.alternativeVendors', {count: alternativePurchaseOptions?.length, price: formattedAlternativePurchaseOptionsLowestPrice}) }}
          </a>
          <div :class="classMerge(
            'flex-col justify-between gap-2.5',
            isAlternativePurchaseOptionsExpanded ? 'flex animate-in fade-in duration-500' : 'hidden'
          )">
            <PurchaseOption v-for="(alternate, index) in alternativePurchaseOptions"
                            :key="alternate.merchant.name || ''"
                            :bauer-product-card-id="productCard.bauerProductCardId"
                            :purchase-option="alternate"
                            :purchase-option-position="index + 2" />
          </div>
        </div>
      </div>
      <Accordion v-if="productCard.description"
                 type="multiple"
                 class="my-3">
        <AccordionItem v-if="isClient"
                       value="desc">
          <AccordionTrigger>{{ $t('commerce.description') }}</AccordionTrigger>
          <AccordionContent class="prose max-w-none font-body text-body-md md:text-body-lg whitespace-pre-wrap">
            {{ productCard.description }}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div class="border-b" />
    </div>
  </div>
</template>
